import { addProtocolIfNeeded } from '@app/utils/addProtocolPrefixIfNeeded';

// Build timestamp injected at build time by Vite - changes on every rebuild
declare const __BUILD_TIMESTAMP__: number;

type LicenseTypeLabelField = 'title' | 'description';

interface AppConfig {
  apiBaseUrl: string;
  ssoLoginPath: string;
  ssoCallbackPath: string;
  ssoOnly?: boolean;
  licenseTypeLabelField: LicenseTypeLabelField;
  /**
   * Optional override for inspection process type.
   * 1 = STANDARD, 2 = BACKOFFICE
   */
  inspectionProcessTypeOverride?: number;
  /**
   * When true, the license note field is required on create/edit when the "mobile app" switch is off.
   */
  licenseNoteRequiredWhenMobileAppFalse?: boolean;
  /**
   * When false, the fisher add/edit modal does not require country (zip/town can be saved without country).
   * Omit or set to true for current default behavior.
   */
  fisherCountryRequired?: boolean;
  /**
   * When true, the fishers table bulk edit mass action is hidden (ACL alone no longer shows it).
   */
  fisherBulkEditHidden?: boolean;
  /**
   * When true, the fisher detail page does not show the work assignments block (and skips loading them).
   */
  fisherWorkAssignmentsHidden?: boolean;
  /**
   * When true, the license detail view hides the fisher phone row under user information.
   * (Use this instead of fisher entrypoint overrides, which also drive dynamic fields and can duplicate phone.)
   */
  licenseViewFisherPhoneHidden?: boolean;
  /**
   * When true, the license detail view hides the language row (common.language) under license information.
   * @default false
   */
  licenseViewLanguageHidden?: boolean;
  /**
   * When true, the license detail view hides the language row (common.language) under license information.
   * @default false
   */
  licenseViewBillingReferenceHidden?: boolean;
  /**
   * When true, the licenses list filter "not printed" is hidden and never sent to the API.
   * @default false
   */
  licensesNotPrintedFilterHidden?: boolean;
  /**
   * When true, the licenses list filter "not formal verified" is hidden and never sent to the API.
   * @default false
   */
  licensesNotFormalVerifiedFilterHidden?: boolean;
  /**
   * Display format for the SaNa number, shown as a placeholder on the SaNa number input in the
   * new fisher modal (e.g. "xxx-xxxx-xxxx/xxx-x"). When empty/undefined, no custom placeholder is used.
   */
  sanaFormat?: string;
  version?: string;
}

const DEFAULT_SSO_LOGIN_PATH = '/sso/login/keycloak';
const DEFAULT_SSO_CALLBACK_PATH = '';
const DEFAULT_LICENSE_TYPE_LABEL_FIELD: LicenseTypeLabelField = 'title';

let config: AppConfig | null = null;

export const loadConfig = async () => {
  try {
    // Use cache: 'no-cache' to ensure we always get the latest config.json
    // This prevents browser/service worker cache from serving stale config
    // Build timestamp in query parameter ensures each build gets fresh config
    const buildVersion = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : Date.now();
    const response = await fetch(`/config.json?v=${buildVersion}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    if (response.ok) {
      const loadedConfig = (await response.json()) as Omit<Partial<AppConfig>, 'inspectionProcessTypeOverride'> & {
        inspectionProcessTypeOverride?: unknown;
        /** @deprecated Use `licenseNoteRequiredWhenMobileAppFalse`. */
        licenseNoteRequiredWhenMobileApp?: boolean;
      };
      const licenseTypeLabelField =
        loadedConfig.licenseTypeLabelField === 'description' ? 'description' : DEFAULT_LICENSE_TYPE_LABEL_FIELD;

      // Ensure apiBaseUrl has protocol prefix (https://) if missing
      const apiBaseUrl = addProtocolIfNeeded(loadedConfig.apiBaseUrl) ?? '';

      const inspectionProcessTypeOverrideRaw = loadedConfig.inspectionProcessTypeOverride;
      const inspectionProcessTypeOverride =
        inspectionProcessTypeOverrideRaw == null || inspectionProcessTypeOverrideRaw === ''
          ? undefined
          : Number(inspectionProcessTypeOverrideRaw);
      const normalizedInspectionProcessTypeOverride =
        Number.isFinite(inspectionProcessTypeOverride) &&
        (inspectionProcessTypeOverride === 1 || inspectionProcessTypeOverride === 2)
          ? inspectionProcessTypeOverride
          : undefined;

      const licenseNoteRequiredWhenMobileAppFalse =
        loadedConfig.licenseNoteRequiredWhenMobileAppFalse !== undefined
          ? loadedConfig.licenseNoteRequiredWhenMobileAppFalse === true
          : loadedConfig.licenseNoteRequiredWhenMobileApp === true;

      config = {
        apiBaseUrl,
        ssoLoginPath: loadedConfig.ssoLoginPath ?? DEFAULT_SSO_LOGIN_PATH,
        ssoCallbackPath: loadedConfig.ssoCallbackPath ?? DEFAULT_SSO_CALLBACK_PATH,
        ssoOnly: loadedConfig.ssoOnly ?? false,
        licenseTypeLabelField,
        inspectionProcessTypeOverride: normalizedInspectionProcessTypeOverride,
        licenseNoteRequiredWhenMobileAppFalse,
        fisherCountryRequired: loadedConfig.fisherCountryRequired !== false,
        fisherBulkEditHidden: loadedConfig.fisherBulkEditHidden === true,
        fisherWorkAssignmentsHidden: loadedConfig.fisherWorkAssignmentsHidden === true,
        licenseViewFisherPhoneHidden: loadedConfig.licenseViewFisherPhoneHidden === true,
        licenseViewLanguageHidden: loadedConfig.licenseViewLanguageHidden === true,
        licenseViewBillingReferenceHidden: loadedConfig.licenseViewBillingReferenceHidden === true,
        licensesNotPrintedFilterHidden: loadedConfig.licensesNotPrintedFilterHidden === true,
        licensesNotFormalVerifiedFilterHidden: loadedConfig.licensesNotFormalVerifiedFilterHidden === true,
        sanaFormat: typeof loadedConfig.sanaFormat === 'string' ? loadedConfig.sanaFormat : undefined,
        version: typeof loadedConfig.version === 'string' ? loadedConfig.version : undefined,
      };

      console.log('[ConfigService] Loaded config:', {
        apiBaseUrl: config.apiBaseUrl,
        rawApiBaseUrl: loadedConfig.apiBaseUrl,
        inspectionProcessTypeOverride: config.inspectionProcessTypeOverride,
      });
    }
  } catch (error) {
    console.warn('Failed to load config.json, using env var fallback');
  }
};

export const getConfig = () => {
  if (!config) {
    console.error('Config not loaded! Make sure config.json exists and loadConfig() was called.');
    return {
      apiBaseUrl: '',
      ssoLoginPath: DEFAULT_SSO_LOGIN_PATH,
      ssoCallbackPath: DEFAULT_SSO_CALLBACK_PATH,
      ssoOnly: false,
      licenseTypeLabelField: DEFAULT_LICENSE_TYPE_LABEL_FIELD,
      inspectionProcessTypeOverride: undefined,
      licenseNoteRequiredWhenMobileAppFalse: false,
      fisherCountryRequired: true,
      fisherBulkEditHidden: false,
      fisherWorkAssignmentsHidden: false,
      licenseViewFisherPhoneHidden: false,
      licenseViewBillingReferenceHidden: false,
      licenseViewLanguageHidden: false,
      licensesNotPrintedFilterHidden: false,
      licensesNotFormalVerifiedFilterHidden: false,
      sanaFormat: undefined,
      version: undefined,
    };
  }
  return config;
};
